import { DependentDto } from 'enrollment/queries/useBenefitCategoryQuery/types';
import { DependentDto as DependentDtoMeta } from 'enrollment/features/MyFamily/types';
import {
  avatarList,
  hairColorPalette,
  shirtColorPalette,
  skinColorPalette,
} from '../../../../app/constants';
import { FamilyDrawerDisplayPicture } from '../../../Family/FamilyDetailsPage/FamilyDrawerDisplayPicture';
import {
  BenefitDto,
  BenefitsResponseDto,
  CoverageDto,
  EnrollmentContextResponseDto,
  EoiContextsDto,
} from 'theSource/queries';

export interface EoiDataDto {
  planNames: string[];
  dependentCoverages: DependentDto[];
  disabled: boolean;
  coverage: CoverageDto;
  orgBenefitId: number;
  popup?: boolean;
}
export interface GroupedEoiData {
  carrierLogoUrl: string;
  carrierName: string;
  orgPlanYearId: number;
  pluginType: string;
  eoiData: EoiDataDto[];
}

export function collectCoveragesPendingEoi(benefitsData?: BenefitsResponseDto) {
  const pendingCoverages: { current: CoverageDto[]; next: CoverageDto[] } = {
    current: [],
    next: [],
  };
  benefitsData?.current?.forEach((benefit) => {
    const coverage = benefit.coverage;

    if ((coverage.waiting_for_eoi && coverage.has_popup_content) || (coverage.waiting_for_eoi && coverage.eoi_data?.visible)) {
      pendingCoverages.current.push(coverage);
    }
  });
  benefitsData?.next?.forEach((benefit) => {
    const coverage = benefit.coverage;
    if ((coverage.waiting_for_eoi && coverage.has_popup_content) || (coverage.waiting_for_eoi && coverage.eoi_data?.visible)) {
      pendingCoverages.next.push(coverage);
    }
  });
  return pendingCoverages;
}

export function useGroupDataByCarrier(
  benefits: BenefitsResponseDto,
  coveragesPendingEoi: { current: CoverageDto[]; next: CoverageDto[] }
): {
  current: { [key: string]: GroupedEoiData };
  next: { [key: string]: GroupedEoiData };
} {
  const retval = {
    current: {},
    next: {},
  };
  const pendingEoiIds = Object.values(coveragesPendingEoi)
    .map((planYearCoverages) => {
      return planYearCoverages.map((coverage) => coverage.req_org_plan_id);
    })
    .flat();

  Object.keys(retval).forEach((key) => {
    const yearKey = key as keyof BenefitsResponseDto;
    const benefitsByYear = (benefits[yearKey] as BenefitDto[]) || [];
    const popupData = benefitsByYear.reduce(
      (groups: { [key: string]: GroupedEoiData }, item) => {
        const carrier = item.coverage?.req_carrier_name;
        if (
          !carrier ||
          !pendingEoiIds.includes(Number(item.coverage?.req_org_plan_id))
        ) {
          return groups;
        }

        if (groups[carrier] === undefined) {
          const newGroup = getNewDataGroup(item, carrier, true);
          groups[carrier] = newGroup;
        } else {
          // each popup needs a new EOIData entry - since each popup is unique
          groups[carrier].eoiData.push({
            planNames: [item.coverage?.org_plan_name || ''],
            dependentCoverages: item.coverage?.dependent_coverages || [],
            disabled: item.coverage?.eoi_data?.disabled || false,
            orgBenefitId: item.org_benefit_id,
            coverage: item.coverage as unknown as CoverageDto,
            popup: true,
          });
        }

        return groups;
      },
      {}
    );

    const indexByCarrier: { [key: string]: number } = {};
    Object.entries(popupData).forEach(([carrier, group]) => {
      indexByCarrier[carrier] = group.eoiData.length;
    });

    const pluginData = benefitsByYear.reduce(
      (groups: { [key: string]: GroupedEoiData }, item) => {
        groups = { ...popupData, ...groups };
        if (Object.keys(item.coverage?.eoi_data || {}).length < 1) {
          return groups;
        }

        const carrier = item.coverage?.req_carrier_name;
        const plugin =
          benefits.eoi[yearKey as keyof EoiContextsDto][
          `${item.coverage?.eoi_data?.plugin_type}`
          ];
        let hidePlanName = false;
        if (!plugin[`${item.coverage.coverage_id}`]) {
          hidePlanName = true;
        }
        if (!carrier || hidePlanName) {
          return groups;
        }
        const group = getEoiPluginDataGroup(
          groups,
          item,
          carrier,
          indexByCarrier[carrier] || 0
        );

        if (group !== undefined) {
          groups[carrier] = group;
        }
        return groups;
      },
      {}
    );

    const retvalKey = key as keyof typeof retval;
    retval[retvalKey] = { ...pluginData, ...popupData };
  });
  return retval;
}

function getEoiPluginDataGroup(
  groups: { [key: string]: GroupedEoiData },
  item: BenefitDto,
  carrier: string,
  index: number
) {
  if (item.coverage?.waiting_for_eoi && item.coverage.eoi_data?.visible) {
    const planName = item.coverage?.org_plan_name;
    const carrierGroup = groups[carrier] || {};

    if (!carrierGroup.carrierName) {
      const newGroup = getNewDataGroup(item, carrier, false);
      groups[carrier] = newGroup;
    } else if (carrierGroup.eoiData[index] === undefined) {
      groups[carrier].eoiData[index] = {
        planNames: [planName],
        dependentCoverages: item.coverage.dependent_coverages,
        disabled: item.coverage.eoi_data?.disabled || false,
        orgBenefitId: item.org_benefit_id,
        coverage: item.coverage as unknown as CoverageDto,
      };
    }

    const group = groups[carrier];
    if (!group.eoiData[index].planNames.includes(planName)) {
      group.eoiData[index].planNames = [
        ...group.eoiData[index].planNames,
        planName,
      ];
    }

    const newDepCovs = item.coverage.dependent_coverages.filter((depCov) => {
      return (
        (depCov.termination_date === null ||
          depCov.termination_date === undefined) &&
        group.eoiData[index].dependentCoverages.find(
          (dc) => dc.dependent_id === depCov.dependent_id
        ) === undefined
      );
    });
    group.eoiData[index].dependentCoverages = [
      ...group.eoiData[index].dependentCoverages,
      ...newDepCovs,
    ];

    return group;
  } else {
    return groups[carrier];
  }
}

const getNewDataGroup = (item: BenefitDto, carrier: string, popup: boolean) => {
  return {
    carrierLogoUrl: item.coverage?.carrier_logo_url || '',
    carrierName: carrier,
    orgPlanYearId: item.org_plan_year_id,
    pluginType: item.coverage?.eoi_data?.plugin_type,
    eoiData: [
      {
        planNames: [item.coverage?.org_plan_name],
        dependentCoverages: item.coverage?.dependent_coverages,
        disabled: item.coverage?.eoi_data?.disabled || false,
        orgBenefitId: item.org_benefit_id,
        coverage: item.coverage as unknown as CoverageDto,
        popup: popup,
      },
    ],
  } as GroupedEoiData;
};

export function getProfileById(id: number, dependents: DependentDtoMeta[]) {
  const dependent = dependents.find(
    (dependent) => dependent.id === id
  )?.dependent_profile;
  if (dependent == null) {
    return null;
  }
  return dependent?.profile_picture
    ? dependent?.profile_picture
    : getAvatarDetails(dependent?.avatar);
}

export function navToPluginEOI(
  params: GroupedEoiData,
  lifeEventCompleted: boolean,
  contexts: EnrollmentContextResponseDto | undefined,
  lumosApiHost: string
) {
  const orgPlanYearId = params.orgPlanYearId;
  const plugin = params.pluginType;
  const context = contexts?.data.find(
    (context) => context.org_plan_year_id === orgPlanYearId
  )?.enrollment_type_lookup_code;
  window.open(
    `${lumosApiHost}/v1/self_service/plugins/eoi_plugin/show_eoi/${plugin}?opy=${orgPlanYearId}&enrollment_code=${context}&life_event_completed=${lifeEventCompleted}`,
    '_blank'
  );
}

export function getAvatarDetails(avatarValues: any) {
  const [picture, skinColor, hairColor, shirtColor] = avatarValues?.split('_');
  const avatarPicture = avatarList.filter(
    (item) => avatarValues && picture === item.name
  );
  const avatarSkincolor = skinColorPalette?.filter(
    (item) => avatarValues && skinColor === item.name
  );
  const avatarHairColor = hairColorPalette?.filter(
    (item) => avatarValues && hairColor === item.name
  );
  const avatarShirtColor = shirtColorPalette?.filter(
    (item) => avatarValues && shirtColor === item.name
  );

  if (avatarPicture.length === 0) {
    return null;
  }

  return (
    <FamilyDrawerDisplayPicture
      saveAvatarName={avatarPicture[0]?.name}
      saveAvatarSkinTone={avatarSkincolor[0]?.colorCode}
      saveAvatarHairColor={avatarHairColor[0]?.colorCode}
      saveAvatarShirtColor={avatarShirtColor[0]?.colorCode}
    />
  );
}
